import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import styles from './FileTabs.module.css';

import { menuPoint } from '../utils/appZoom';
/**
 * 프로젝트 선택(개별스크립트 + 열린 프로젝트) — 탭 줄 맨 왼쪽, 또는 편집기 툴바의 검색 왼편(App, 2026-10-22).
 * inline 이면 탭 줄용 구분선·아래 여백 없이 툴바 단추처럼 선다.
 */
export const ProjectPicker = ({ projects = [], activeProjectId = null, onSwitchProject, onCloseProject, inline = false }) => {
    const { t } = useTranslation();
    return (
        <div className={inline ? `${styles.projectPicker} ${styles.projectPickerInline}` : styles.projectPicker}>
            <select
                className={styles.projectSelect}
                value={activeProjectId || '__default__'}
                onChange={(e) => onSwitchProject(e.target.value === '__default__' ? null : e.target.value)}
                title={t('common:ft.pickProject')}
            >
                {/* 개별스크립트 — projectId 가 없는 탭들의 그룹, 항상 표시 */}
                <option value="__default__">{t('common:ft.standalone')}</option>
                {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
            {activeProjectId && typeof onCloseProject === 'function' && (
                <button
                    className={styles.projectCloseBtn}
                    title={t('common:ft.closeProject')}
                    onClick={() => onCloseProject(activeProjectId)}
                >
                    <FiX size={11} />
                </button>
            )}
        </div>
    );
};

const FileTabs = ({
    openFiles,
    activeFileId,
    onTabClick,
    onTabClose,
    onNewTab,
    onTabRename,
    // 파일 › 새 파일 즐겨찾기를 끄면 + 버튼만 숨긴다 — Alt+N 과 메뉴는 그대로 동작한다
    showNewTabButton = true,
    // 다중 프로젝트 지원 (선택 props) — 미제공이면 기존 단일 탭바 동작
    projects = [],
    activeProjectId = null,
    onSwitchProject,
    onCloseProject,
    // 여러 탭을 한 번에 닫기(우클릭 메뉴) — 고친 탭이 있으면 App 이 확인을 받는다
    onCloseTabs,
    // 끌어서 순서 바꾸기 — (끈 탭, 놓은 자리의 탭, 그 탭 앞인가)
    onTabReorder,
    // 탭 복제(우클릭, 2026-10-26) — 같은 내용·세션의 새 탭을 바로 오른쪽에
    onDuplicateTab,
    // 닫은 탭 다시 열기(우클릭·닫을 때 알림의 「되돌리기」) — canReopen 이 거짓이면 메뉴가 꺼진다
    onReopenClosed,
    canReopen = false,
    // 탭 줄 수(1 = 가로 스크롤, 2·3 = 여러 줄로 바꿈) — 편집기 옵션에 저장(App)
    tabRows = 1,
    onTabRowsChange,
}) => {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const inputRef = useRef(null);
    // 우클릭 메뉴 { x, y, fileId }
    const [menu, setMenu] = useState(null);
    const menuRef = useRef(null);
    // 끌기 — 끄는 탭과 놓일 자리 표시 { id, before }
    const [dragId, setDragId] = useState(null);
    const [dropAt, setDropAt] = useState(null);

    // 메뉴는 바깥을 누르거나 Esc·창 크기 변경·창 전환이면 닫는다
    useEffect(() => {
        if (!menu) return undefined;
        const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null); };
        const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
        const close = () => setMenu(null);
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', close);
        window.addEventListener('blur', close);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', close);
            window.removeEventListener('blur', close);
        };
    }, [menu]);

    useEffect(() => {
        if (editingId && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingId]);

    const startEditing = (file) => {
        setEditingId(file.id);
        setEditText(file.name.replace(/\.sql$/, ''));
    };

    const finishEditing = () => {
        if (editingId && editText.trim()) {
            // 프로젝트 소속 탭은 .sql 확장자를 붙이지 않음 (기본 작업공간 탭만 자동 부착)
            const editingFile = (openFiles || []).find(f => f.id === editingId);
            const isProjectTab = !!(editingFile && editingFile.projectId);
            const trimmed = editText.trim();
            const newName = isProjectTab
                ? trimmed
                : (trimmed.endsWith('.sql') ? trimmed : trimmed + '.sql');
            if (onTabRename) onTabRename(editingId, newName);
        }
        setEditingId(null);
    };

    const showProjectSelector = typeof onSwitchProject === 'function';

    // ── 우클릭 메뉴 ── 대상은 **보이는 탭(이 프로젝트의 탭)** 안에서만 — 다른 프로젝트의 탭을 닫지 않는다
    const openMenu = (e, file) => {
        e.preventDefault();
        // 화면 밖으로 나가지 않게 오른쪽·아래 끝에서 안으로 당긴다(메뉴 크기 어림값)
        // 화면 확대 중이면 마우스 좌표를 CSS px 로(utils/appZoom)
        const { x, y } = menuPoint(e, 200, 180, e.view || window);
        setMenu({ x, y, fileId: file.id });
    };
    const menuIdx = menu && menu.fileId ? openFiles.findIndex(f => f.id === menu.fileId) : -1;
    // 탭이 아닌 빈 자리를 우클릭하면 탭에 매이지 않은 항목(닫은 탭 다시 열기 · 탭 줄 수)만 쓸 수 있다
    const openBarMenu = (e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        const { x, y } = menuPoint(e, 200, 180, e.view || window);
        setMenu({ x, y, fileId: null });
    };
    const runMenu = (action) => {
        const file = openFiles[menuIdx];
        setMenu(null);
        if (action === 'reopen') { if (onReopenClosed) onReopenClosed(); return; }
        if (/^rows[123]$/.test(action)) { if (onTabRowsChange) onTabRowsChange(Number(action.slice(4))); return; }
        if (!file) return;
        if (action === 'duplicate') { if (onDuplicateTab) onDuplicateTab(file.id); return; }
        if (action === 'rename') { startEditing(file); return; }
        if (action === 'close') { onTabClose(file.id); return; }
        const ids = action === 'others' ? openFiles.filter(f => f.id !== file.id).map(f => f.id)
            : action === 'right' ? openFiles.slice(menuIdx + 1).map(f => f.id)
            : openFiles.map(f => f.id);   // 'all'
        if (ids.length > 0 && onCloseTabs) onCloseTabs(ids);
    };

    // ── 끌어서 순서 바꾸기 ── 같은 탭 줄(= 같은 프로젝트) 안에서만 옮긴다
    const onDragStart = (e, file) => {
        if (editingId) { e.preventDefault(); return; }
        setDragId(file.id);
        e.dataTransfer.effectAllowed = 'move';
        // 파이어폭스는 데이터가 없으면 끌기를 시작하지 않는다
        try { e.dataTransfer.setData('text/plain', file.id); } catch (_e) { /* 무시 */ }
    };
    const onDragOver = (e, file) => {
        if (!dragId || dragId === file.id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const r = e.currentTarget.getBoundingClientRect();
        const before = e.clientX < r.left + r.width / 2;
        if (!dropAt || dropAt.id !== file.id || dropAt.before !== before) setDropAt({ id: file.id, before });
    };
    const endDrag = () => { setDragId(null); setDropAt(null); };
    const onDrop = (e, file) => {
        e.preventDefault();
        if (dragId && dropAt && dragId !== file.id && onTabReorder) onTabReorder(dragId, file.id, dropAt.before);
        endDrag();
    };

    return (
        // 프로젝트 선택은 **스크롤 영역 밖**(2026-10-07) — 탭만 가로로 스크롤하고 스크롤바도 탭 아래에만 그려진다.
        // 예전에는 한 스크롤 영역 안에 있어 탭이 넘치면 선택기까지 밀려 사라졌다.
        <div className={styles.tabsBar}>
            {showProjectSelector && (
                <ProjectPicker projects={projects} activeProjectId={activeProjectId}
                    onSwitchProject={onSwitchProject} onCloseProject={onCloseProject} />
            )}
            {/* 새 탭 추가 — 탭 줄 맨 왼쪽, **스크롤 영역 밖**(탭이 많아도 늘 보인다, 2026-10-26). 새 탭은 좌측에 삽입되어 기존 탭을 오른쪽으로 밀어냄 */}
            {showNewTabButton && (
                <div className={styles.newTabFixed}>
                    <button className={styles.newTabButton} onClick={onNewTab} title={t('common:ft.newTab')}>+</button>
                </div>
            )}
            <div className={tabRows > 1 ? `${styles.tabsContainer} ${styles.tabsWrap}` : styles.tabsContainer} role="tablist"
                style={tabRows > 1 ? { '--ft-rows': tabRows } : undefined} onContextMenu={openBarMenu}>
            {openFiles.map(file => (
                <div
                    key={file.id}
                    role="tab"
                    aria-selected={file.id === activeFileId}
                    className={[
                        styles.tabItem,
                        file.id === activeFileId ? styles.activeTab : '',
                        dragId === file.id ? styles.tabDragging : '',
                        dropAt && dropAt.id === file.id ? (dropAt.before ? styles.dropBefore : styles.dropAfter) : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => onTabClick(file.id)}
                    onDoubleClick={() => startEditing(file)}
                    onContextMenu={(e) => openMenu(e, file)}
                    draggable={!!onTabReorder && editingId !== file.id}
                    onDragStart={(e) => onDragStart(e, file)}
                    onDragOver={(e) => onDragOver(e, file)}
                    onDrop={(e) => onDrop(e, file)}
                    onDragEnd={endDrag}
                    title={t('common:ft.dblClickRename')}
                >
                    {editingId === file.id ? (
                        <input
                            ref={inputRef}
                            className={styles.tabNameInput}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onBlur={finishEditing}
                            onKeyDown={(e) => { if (e.key === 'Enter') finishEditing(); if (e.key === 'Escape') setEditingId(null); }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <span className={styles.tabName} title={file.name}>{file.name.replace(/\.sql$/i, '')}{file.isDirty ? '*' : ''}</span>
                    )}
                    {openFiles.length > 0 && (
                        <button
                            className={styles.closeButton}
                            onClick={(e) => { e.stopPropagation(); onTabClose(file.id); }}
                            title={t('common:ft.close')}
                            aria-label={t('common:ft.close')}
                        >
                            <FiX size={12} />
                        </button>
                    )}
                </div>
            ))}
            {menu && (menuIdx >= 0 || !menu.fileId) && (
                <div
                    ref={menuRef}
                    className={styles.tabMenu}
                    style={{ left: menu.x, top: menu.y }}
                    role="menu"
                >
                    {menuIdx >= 0 && <>
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('rename')}>{t('common:ft.rename')}</button>
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('duplicate')} disabled={!onDuplicateTab}>{t('common:ft.duplicate')}</button>
                    <div className={styles.tabMenuSep} />
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('close')}>{t('common:ft.close')}</button>
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('others')}
                        disabled={!onCloseTabs || openFiles.length < 2}>{t('common:ft.closeOthers')}</button>
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('right')}
                        disabled={!onCloseTabs || menuIdx >= openFiles.length - 1}>{t('common:ft.closeRight')}</button>
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('all')}
                        disabled={!onCloseTabs}>{t('common:ft.closeAll')}</button>
                    <div className={styles.tabMenuSep} />
                    </>}
                    <button type="button" role="menuitem" className={styles.tabMenuItem} onClick={() => runMenu('reopen')}
                        disabled={!onReopenClosed || !canReopen}>{t('common:ft.reopenClosed')}</button>
                    {onTabRowsChange && <>
                        <div className={styles.tabMenuSep} />
                        <div className={styles.tabMenuGroup}>{t('common:ft.rowsTitle')}</div>
                        {[1, 2, 3].map(n => (
                            <button key={n} type="button" role="menuitemradio" aria-checked={tabRows === n} className={styles.tabMenuItem} onClick={() => runMenu(`rows${n}`)}>
                                <span className={styles.tabMenuCheck}>{tabRows === n ? '✓' : ''}</span>{t(`common:ft.rows${n}`)}
                            </button>
                        ))}
                    </>}
                </div>
            )}
            </div>
        </div>
    );
};

export default FileTabs;
